import React, { Suspense, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import env from 'react-dotenv';
import 'tailwindcss/dist/tailwind.min.css';
import './index.css';

import packageInfo from '../package.json';
import Loader, { LoaderWrapper } from './components/common/Loader';
import { addGTag } from './utilities/gtag';
import App from './App';

import { PosthogProvider } from './providers/PosthogProvider';
import CacheBuster from 'react-cache-buster';
import { CookiesProvider } from 'react-cookie';
import { IntlProvider } from 'react-intl';
import { addAWINScript } from './data/gtm';

for (const [key, value] of Object.entries(env)) {
    console.log(`${key}: ${value}`);
    if (value === 'true') env[key] = true;
    if (value === 'false') env[key] = false;
    if (value === 'null') env[key] = null;
    if (!isNaN(Number(value))) env[key] = Number(value);
}

console.log('ENV', env);


// if (env.SENTRY_ENABLED) {
//     init({
//         dsn: 'https://36eb73745f2f45a49f9b33e3afe33120@o1031878.ingest.sentry.io/6002013',
//         integrations: [new BrowserTracing()],
//         environment: env.ENV_NAME,
//         // Set tracesSampleRate to 1.0 to capture 100%
//         // of transactions for performance monitoring.
//         // We recommend adjusting this value in production
//         tracesSampleRate: 0.1,
//     });
// }

const initSentry = async () => {
    if (!env.SENTRY_ENABLED) return;

    const Sentry = await import('@sentry/react');
    Sentry.init({
        dsn: 'https://36eb73745f2f45a49f9b33e3afe33120@o1031878.ingest.sentry.io/6002013',
        integrations: [new Sentry.BrowserTracing()],
        environment: env.ENV_NAME,
        tracesSampleRate: 0.1,
    });
};

initSentry();

if (env.AWIN_ENABLED) {
    addAWINScript();
}

const host = window.location.hostname;
const domainSuffix = host === 'localhost' ? 'com' : host.split('.').pop();

if (env.GTM_ENABLED && env.GTAG_ID) {
    // const gtagId = env.GTAG_ID;

    if (host !== 'localhost' &&  !host.endsWith('ngrok-free.app')) {
        addGTag();
    }
}

const locale = navigator.language.substr(0, 2).toLowerCase();

const loadLocaleData = async (locale) => {
    switch (locale) {
        case 'pl':
            return await import(/* webpackChunkName: 'lang_pl' */ './i18n/pl.json');
        case 'de':
            return await import(/* webpackChunkName: 'lang_de' */ './i18n/de.json');
        case 'fr':
            return await import(/* webpackChunkName: 'lang_fr' */ './i18n/fr.json');
        case 'it':
            return await import(/* webpackChunkName: 'lang_it' */ './i18n/it.json');
        case 'da':
            return await import(/* webpackChunkName: 'lang_da' */ './i18n/da.json');
        case 'nl':
            return await import(/* webpackChunkName: 'lang_nl' */ './i18n/nl.json');
        case 'sv':
            return await import(/* webpackChunkName: 'lang_sv' */ './i18n/sv.json');
        default:
            return await import(/* webpackChunkName: 'lang_en' */ './i18n/en.json');
    }
};

document.documentElement.setAttribute('lang', locale);

const Root = () => {
    const [messages, setMessages] = useState(null);

    useEffect(() => {
        // Ładowanie tłumaczeń
        loadLocaleData(locale).then((data) => {
            setMessages(data.default);
        });
    }, []);

    if (!messages) {
        return (
            <LoaderWrapper $overlap={true}>
                <Loader />
            </LoaderWrapper>
        );
    }

    return (
            <IntlProvider locale={locale} messages={messages}>
                    <PosthogProvider 
                        apiKey={env.REACT_APP_PUBLIC_POSTHOG_KEY}
                        apiHost={`https://api2.framky.${domainSuffix}`}
                        options={{
                            autocapture: false,
                            disabled_heatmaps: true,
                            persistence: 'localStorage+cookie',
                            person_profiles: 'always',
                        }}
                    >
                            <CookiesProvider>
                                    <CacheBuster
                                        currentVersion={packageInfo.version}
                                        isEnabled={env.ENV_NAME === 'Production'}
                                        isVerboseMode={false} //If true, the library writes verbose logs to console.
                                        metaFileDirectory={'../public'}
                                        loadingComponent={
                                            <LoaderWrapper $overlap>
                                                <Loader />
                                            </LoaderWrapper>
                                        }
                                    >
                                        <App />
                                    </CacheBuster>
                            </CookiesProvider>
                    </PosthogProvider>
            </IntlProvider>
    );
};

createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <Suspense fallback={<LoaderWrapper $overlap={true}><Loader /></LoaderWrapper>}>
            <Root />
        </Suspense>
    </React.StrictMode>
);
